// askSmart-ui/src/pages/TOSP/components/LeaseReview/LeaseReviewWorkspace.tsx
import React, { useEffect, useRef, useState } from "react";
import { OccupancyDraft, draftReview, draftText, reviewSeed } from "../../occupancyDraft";
import { formatDate } from "../../format";
import { LeaseReviewStamp, RENT_STEP_UP_MONTHS, Tenant } from "../../types";
import { fingerprintLease } from "./leaseFingerprint";
import LeasePdfPane from "./LeasePdfPane";
import {
  AmendmentApply,
  AmendmentExtraction,
  DetectedSpace,
  displayValue,
  extractAmendment,
  extractLease,
  FieldReviewState,
  LEASE_REVIEW_FIELDS,
  LeaseFieldExtraction,
  LeaseReviewField,
  sameValue,
  spaceFieldKey,
} from "./leaseReviewFields";
import { fetchStoredLease, resolveStoredLease } from "./leaseSource";

/** The amendment panel's rows, named once so both the panel and the restore
 *  path mean the same four things. */
const AMEND_KEYS = ["amendCommence", "amendPsf", "amendNotes", "amendUseExtension"];

/**
 * An amendment already applied, read back off the occupancy.
 *
 * Used when the admin reopens an amendment that has been confirmed before, so
 * the panel shows what they signed off rather than starting blank.
 *
 * The renewal is looked for by its `fromAmendment` mark, falling back to the
 * space's most recent renewal. The fallback matters: renewals written before
 * that mark existed carry no trace of where they came from, and the last one
 * on a lease being reopened as an amendment is the one the amendment made.
 * Confirming the reopened review marks it, so the fallback is needed once per
 * lease and never again.
 */
function appliedAmendment(draft: OccupancyDraft): {
  resetId: string;
  rentCommenceDate: string;
  rentPsfCommencement: string;
  extensionNotes: string;
  usedExtension: boolean;
} {
  const space = draft.leasedSpaces[0];
  const resets = space?.rentResets || [];
  const found =
    [...resets].reverse().find((reset) => reset.fromAmendment) ||
    resets[resets.length - 1];

  return {
    resetId: found?.id || "",
    rentCommenceDate: found?.rentCommenceDate || "",
    rentPsfCommencement: found?.rentPsfCommencement || "",
    extensionNotes: draftText(draft, "extensionNotes"),
    // Whether THIS amendment took up the extension isn't recorded separately —
    // only the running count is. An amendment being reopened on a lease with
    // one taken up is the one that took it.
    usedExtension: (draft.extensionTermsUsed ?? 0) > 0,
  };
}

interface LeaseReviewWorkspaceProps {
  tenant: Tenant | null;
  /** The occupancy being reviewed, so the form's current values seed the review. */
  draft: OccupancyDraft;
  /**
   * The tenant-level fields as they stand in the form — not as they were last
   * saved. An admin who has just typed a name and gone straight to the review
   * would otherwise be told the lease disagrees with a blank.
   */
  tenantValues: Record<string, string>;
  /**
   * The lease already in hand, when the caller has it.
   *
   * The Add Tenant wizard reads the document to find out which spaces it
   * covers, so by the time this screen opens the bytes are downloaded and
   * there is nothing to gain by fetching an eighty-page PDF a second time.
   * Left out, the lease is looked up the usual way.
   */
  initialBytes?: Uint8Array | null;
  /** Picks this occupancy's lease out of the tenant's other ones. */
  unitSuite?: string;
  /** Recorded on the review stamp, so a later reviewer knows who signed it. */
  reviewerName?: string;
  onCancel: () => void;
  /**
   * The reviewed values, to be written onto the occupancy draft, and a stamp
   * of what was signed off against which document.
   *
   * `amendment` is present only when the open document was an amendment (LAA):
   * the values and stamp still ride along (the terms already verified are kept),
   * and the amendment carries the handful of changes to apply on top.
   */
  onFinish: (
    values: Record<string, string>,
    review: LeaseReviewStamp,
    amendment?: AmendmentApply,
  ) => void;
}

// Reviewing a lease: the document on the left, its terms on the right, every
// term listed at once.
//
// The admin is confirming what the lease says, not filling in a form, so the
// screen is given over to it entirely and the list is the whole interface —
// each row shows where it stands, and the ones still wanting attention are
// visible without stepping through the ones that don't. Nothing is committed
// until every row has been looked at: the record ends up carrying the admin's
// judgement, not the extractor's.
export default function LeaseReviewWorkspace({
  tenant,
  draft,
  tenantValues,
  initialBytes,
  unitSuite,
  reviewerName,
  onCancel,
  onFinish,
}: LeaseReviewWorkspaceProps) {
  const [bytes, setBytes] = useState<Uint8Array | null>(initialBytes ?? null);
  const [leaseLabel, setLeaseLabel] = useState<string>("");
  /** Why no stored lease is open, when there isn't one. */
  const [lookup, setLookup] = useState<string | null>(null);
  const [loading, setLoading] = useState(!initialBytes);
  const [extraction, setExtraction] = useState<LeaseFieldExtraction>({});
  /** The areas the lease covers, in the order it states them. */
  const [spaces, setSpaces] = useState<DetectedSpace[]>([]);
  /** Fields the admin has decided on. Save waits for all of them. */
  const [reviewed, setReviewed] = useState<Set<string>>(new Set());
  /** The row showing its Keep / Update choice, if any. */
  const [openKey, setOpenKey] = useState<string | null>(null);
  /** Whose anchor the document pane is showing. */
  const [focusKey, setFocusKey] = useState<string | null>(null);
  /** Bumped on every jump, so reopening the same row scrolls back to it. */
  const [jump, setJump] = useState(0);
  /** Identifies the open document against the last review's. */
  const [fingerprint, setFingerprint] = useState<string | null>(null);
  /** Set once extraction has run, so restoring can tell "none" from "not yet". */
  const [extracted, setExtracted] = useState(false);
  /** How the last review was carried forward, for the line above the list. */
  const [carried, setCarried] = useState<{ at: string; again: number } | null>(null);
  // The amendment read off the open document, when it is one. Null until the
  // classifier has run and said so — an amendment is only ever considered when
  // the document has changed since the last review (see the effect below).
  const [amendment, setAmendment] = useState<AmendmentExtraction | null>(null);
  const [amendCommence, setAmendCommence] = useState("");
  const [amendPsf, setAmendPsf] = useState("");
  const [amendNotes, setAmendNotes] = useState("");
  const [amendUseExtension, setAmendUseExtension] = useState(true);
  /**
   * The renewal this amendment already put on the record, when reopening one.
   *
   * Empty for a new amendment. Carried back on Verify so the same renewal is
   * corrected rather than a second one added beside it.
   */
  const [amendResetId, setAmendResetId] = useState("");
  /**
   * The amendment rows signed off, and which one is open.
   *
   * Kept apart from `reviewed` on purpose: in amendment mode the lease's own
   * terms are all carried as verified, so a shared set would read as complete
   * the moment the mode engaged and the Verify button would never be a gate.
   */
  const [reviewedAmend, setReviewedAmend] = useState<Set<string>>(new Set());
  const [openAmendKey, setOpenAmendKey] = useState<string | null>(null);
  /** True while the amendment is being read — seconds, if it needs the pages. */
  const [readingAmendment, setReadingAmendment] = useState(false);
  /** Scrolled to when amendment mode opens — the work is at the foot of a long
   *  list of green rows, and a screen that lands at the top hides it. */
  const amendmentRef = useRef<HTMLDivElement | null>(null);
  // The escape hatch: the admin says the changed document is a replacement
  // lease, not an amendment, and wants every term checked again.
  const [fullReverify, setFullReverify] = useState(false);
  /**
   * The admin has said this document is an amendment already applied.
   *
   * Needed for every review signed off before the stamp recorded which kind it
   * was: the record carries no trace, and inferring it from a rent renewal
   * being present would guess wrong on a lease whose renewals were typed in.
   */
  const [forceAmendment, setForceAmendment] = useState(false);
  const amendmentRestored = useRef(false);
  const restored = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  /** What this occupancy signed off last time, if anything. */
  const previous = draftReview(draft);
  /** Extensions the lease grants that haven't been marked used yet. */
  const unusedExtensions =
    draft.extensionTermEnabled &&
    (draft.extensionTermsUsed ?? 0) <
      draft.extensionTerms.filter((t) => t.trim()).length;
  // A different document over an already-verified occupancy is treated as an
  // amendment by default: the review is only ever reopened to apply an LAA, so
  // the verified terms are kept and only the changes are asked for. The rare
  // replacement lease is handled by the "re-verify everything" escape hatch,
  // not by a fragile guess at the document's wording.
  //
  // Reopening an amendment ALREADY applied counts too — same document, same
  // hash, but the last thing signed off was an amendment. A lease can be
  // re-reviewed whenever the admin wants; an amendment has to work the same
  // way, showing its confirmed rows rather than vanishing.
  // Reopening an amendment already applied. The admin saying so is enough on
  // its own — they are looking at the document, and the record may carry
  // nothing that tells one kind of review from the other. Everything else here
  // is inference; that is a statement of fact, so it doesn't get qualified.
  const reopeningAmendment = Boolean(
    forceAmendment ||
      (previous?.amendment && fingerprint && fingerprint === previous.documentHash),
  );
  const amendmentMode = Boolean(
    !fullReverify &&
      (reopeningAmendment ||
        (previous && fingerprint && fingerprint !== previous.documentHash)),
  );

  // Seeded from the record as it stands, so a review of a part-filled one
  // starts from what's already known rather than blanking it. Each field is
  // read from whichever record it belongs to.
  const seed = () =>
    LEASE_REVIEW_FIELDS.reduce(
      (acc, field) => ({
        ...acc,
        [field.key]:
          field.scope === "tenant"
            ? tenantValues[field.key] || ""
            : reviewSeed(draft, field.key),
      }),
      {} as Record<string, string>,
    );
  const [values, setValues] = useState<Record<string, string>>(seed);
  // What was on the record before the lease was read, kept so a disagreement
  // between the two can be shown as a proposed change rather than applied.
  const [recorded] = useState<Record<string, string>>(seed);

  // A rate box for each area beyond the first. Built from the document rather
  // than hardcoded, so a lease with a mezzanine gets a mezzanine box.
  const spaceRows: LeaseReviewField[] = spaces.slice(1).map((space) => ({
    key: spaceFieldKey(space.id, "rentPsfCommencement"),
    label: `${space.name} — Rent PSF`,
    kind: "money",
    hint: `Minimum Rent per square foot for the ${space.name} at the start of the term.`,
  }));
  const rows = [...LEASE_REVIEW_FIELDS, ...spaceRows];

  /**
   * Opens the amendment panel over an amendment that has already been applied.
   *
   * Reached two ways. Automatically, when the last review recorded itself as an
   * amendment; and by hand, from the banner, when it didn't — every review
   * signed off before the stamp carried that flag looks exactly like a lease
   * review, and the admin is the one who knows which it was.
   *
   * The figures come off the record rather than the document: the record holds
   * what was confirmed, and re-reading a flattened LAA would spend a vision
   * call to arrive at a worse answer. Every row lands confirmed — they were —
   * and the pencil still opens any of them to correct.
   */
  const openAppliedAmendment = () => {
    const applied = appliedAmendment(draft);
    setAmendResetId(applied.resetId);
    setAmendCommence(applied.rentCommenceDate);
    setAmendPsf(applied.rentPsfCommencement);
    setAmendNotes(applied.extensionNotes);
    setAmendUseExtension(applied.usedExtension);
    setReviewedAmend(new Set(AMEND_KEYS));
    // The lease's own terms are carried as they stand, so the gate is the
    // amendment panel rather than the list above it.
    setReviewed(new Set(rows.map((f) => f.key)));
    setFullReverify(false);
    setForceAmendment(true);
    // Both restore effects are done with: this has taken their place.
    amendmentRestored.current = true;
    restored.current = true;
    requestAnimationFrame(() =>
      amendmentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };

  // The signed lease, found through the tenant's primary contact. When a hop
  // in that chain fails the reason is shown rather than swallowed — "no lease"
  // and "the contact isn't a SMRT Docs user" need different things done about
  // them.
  //
  // Skipped entirely when the caller already has the document in hand.
  useEffect(() => {
    if (initialBytes) return;
    if (!tenant) {
      setLookup("Save the tenant before reviewing a lease.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    (async () => {
      const result = await resolveStoredLease(
        tenant,
        unitSuite,
        draftText(draft, "leaseDocumentKey"),
      );
      if (cancelled) return;

      if (result.status === "missing") {
        setLookup(result.reason);
        setLoading(false);
        return;
      }

      if (result.status === "choose") {
        // Reached only if this workspace is opened on a tenancy that was never
        // tied to a service — the Review Lease flow asks first and records the
        // answer, so normally the question is already settled by here.
        setLookup(
          `This contact holds ${result.services.length} leases and this tenancy isn't ` +
            `linked to one yet (${result.services
              .map((service) => `“${service.serviceName}”`)
              .join(", ")}). Run Review Lease from the Occupancy card to choose.`,
        );
        setLoading(false);
        return;
      }

      try {
        const data = await fetchStoredLease(result.lease);
        if (cancelled) return;
        setBytes(data);
        setLeaseLabel(result.lease.label);
        setLookup(null);
      } catch (err) {
        if (cancelled) return;
        setLookup(err instanceof Error ? err.message : "Couldn't open the lease.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, unitSuite, initialBytes]);

  // Extraction runs over whatever document is open. A field with no rule, or a
  // rule that finds nothing, opens as "not found" and the reviewer keys it in —
  // the review flow is identical either way, which is what lets the rules be
  // added one field at a time.
  //
  // What it may and may not do is the whole point:
  //
  //   blank field  → the lease's value is filled in as a suggestion
  //   filled field → nothing is touched; the difference is put to the reviewer
  //
  // An extractor that silently corrected an admin's own entry would make the
  // record unauditable — nobody could tell afterwards which figures a person
  // decided and which a parser did.
  useEffect(() => {
    if (!bytes) return;
    let cancelled = false;

    void fingerprintLease(bytes).then((print) => {
      if (!cancelled) setFingerprint(print);
    });

    extractLease(bytes).then((found) => {
      if (cancelled) return;

      // The first area the lease states is the main commercial one, and its
      // rent is what the rows above are about — rent commencement, the
      // escalation bracket and the step-up month are the lease's, stated once
      // against that area. Every OTHER area gets a rate box of its own, since
      // a basement at $15.00 and a terrace at $54.08 are different figures the
      // record has to carry.
      const flat: LeaseFieldExtraction = { ...found.fields };
      for (const space of found.spaces.slice(1)) {
        const psf = space.values.rentPsfCommencement;
        if (psf) flat[spaceFieldKey(space.id, "rentPsfCommencement")] = psf;
      }

      setSpaces(found.spaces);
      setExtraction(flat);
      setValues((prev) => {
        const next = { ...prev };
        for (const [key, value] of Object.entries(flat)) {
          if (!next[key]?.trim()) next[key] = value.value;
        }
        return next;
      });
      setExtracted(true);
    });

    return () => {
      cancelled = true;
    };
  }, [bytes]);

  // How the lease and the record stand relative to each other, per field.
  const stateOf = (key: string): FieldReviewState => {
    const suggested = extraction[key]?.value;
    if (!suggested?.trim()) return { kind: "none" };

    const was = (recorded[key] || "").trim();
    // Nothing was on the record, so the suggestion filled it — see above.
    if (!was) return { kind: "suggested" };
    if (sameValue(was, suggested)) return { kind: "agrees" };
    return { kind: "conflict", recorded: was, suggested };
  };

  // Carries the last review forward, when this is the same document.
  //
  // Waits for extraction, because a field can only be carried forward once
  // it's known whether the lease still agrees with it. A term signed off last
  // time that the record has since been edited away from is a real difference,
  // and marking it green would bury exactly the thing worth seeing.
  //
  // Runs once. After that the marks belong to the admin, and an effect that
  // re-ran would undo decisions they'd just made.
  useEffect(() => {
    if (restored.current || !extracted || !fingerprint) return;
    if (!previous || previous.documentHash !== fingerprint) return;
    restored.current = true;

    const carriedKeys = previous.verified.filter(
      (key) => stateOf(key).kind !== "conflict",
    );
    setReviewed(new Set(carriedKeys));
    setCarried({
      at: previous.reviewedAt,
      again: rows.length - carriedKeys.length,
    });
    // stateOf reads state this effect deliberately runs after; the guard above
    // is what keeps it to a single pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extracted, fingerprint]);

  // A changed document may be an amendment (LAA) rather than a replacement
  // lease. When it classifies as one, the terms already verified are kept — the
  // rows are marked reviewed so they read green and the gate is satisfied — and
  // only the amendment's own changes are put to the reviewer, in the panel
  // below. A replacement lease (different document, not an amendment) falls
  // through to the ordinary "check every term again" path.
  useEffect(() => {
    if (amendmentRestored.current) return;
    if (!bytes || !fingerprint || !previous) return;
    const reopening = fingerprint === previous.documentHash;
    // Same document, and the last review was of the lease itself — not an
    // amendment at all. The ordinary carry-forward below handles it.
    if (reopening && !previous.amendment) return;
    amendmentRestored.current = true;
    restored.current = true; // keep the stale-document carry-forward from also firing
    // The verified terms are carried as they stand — every row marked reviewed
    // so the list reads green and the gate is the amendment panel below.
    setReviewed(new Set(rows.map((f) => f.key)));

    // Reopening an amendment already applied — the same thing the banner's
    // link does by hand, done automatically because the stamp says so.
    if (reopening) {
      openAppliedAmendment();
      return;
    }

    setAmendUseExtension(unusedExtensions);
    let cancelled = false;
    // Prefill the panel from the document. `isAmendment` only sharpens the
    // banner wording now — it no longer gates the mode, so a low-confidence read
    // still opens the panel for the admin to fill in.
    setReadingAmendment(true);
    extractAmendment(bytes).then((found) => {
      if (cancelled) return;
      setReadingAmendment(false);
      setAmendment(found);
      setAmendCommence(found.rentCommenceDate?.value || "");
      setAmendPsf(found.rentPsfCommencement?.value || "");
      setAmendNotes(found.extensionNotes?.value || "");
      // Land on the work. Every lease row above reads green in amendment mode,
      // so a screen that opens at the top looks finished — the only thing
      // actually being asked for is out of sight at the bottom.
      requestAnimationFrame(() =>
        amendmentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      );
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bytes, fingerprint]);

  /** Opens a row's choice and scrolls the document to where the value came from. */
  const open = (key: string) => {
    setOpenKey((current) => (current === key ? null : key));
    setFocusKey(key);
    setJump((n) => n + 1);
  };

  // A decision. The row closes on the way out: it's been dealt with, and
  // leaving it open would mean the list never shortens as the work is done.
  const settle = (key: string, take: string) => {
    setValues((prev) => ({ ...prev, [key]: take }));
    setReviewed((prev) => new Set(prev).add(key));
    setOpenKey(null);
  };

  const setValue = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  /**
   * The amendment's own rows, read and confirmed the same way a lease term is.
   *
   * Deliberately the same shape and the same interaction as the list above —
   * an admin confirming an amendment is doing the identical job on a shorter
   * list, and a second style of control to learn earns nothing.
   */
  const AMEND_ROWS: {
    key: string;
    label: string;
    kind: "date" | "money" | "textarea" | "choice";
    value: string;
    setValue: (v: string) => void;
    found?: string;
    /** Where the value was read from, so the pencil can jump to it. */
    anchor?: LeaseFieldExtraction[string]["anchor"];
    hint: string;
  }[] = [
    {
      key: "amendCommence",
      label: "New Rent Commencement Date",
      kind: "date",
      value: amendCommence,
      setValue: setAmendCommence,
      found: amendment?.rentCommenceDate?.value,
      anchor: amendment?.rentCommenceDate?.anchor,
      hint: "The date the amended rent starts — added as a new rent commencement, exactly as the “+” on the space does.",
    },
    {
      key: "amendPsf",
      label: "New Rent PSF Commencement",
      kind: "money",
      value: amendPsf,
      setValue: setAmendPsf,
      found: amendment?.rentPsfCommencement?.value,
      anchor: amendment?.rentPsfCommencement?.anchor,
      hint: "The amended rate per square foot from that date.",
    },
    {
      key: "amendNotes",
      label: "Extension Notes",
      kind: "textarea",
      value: amendNotes,
      setValue: setAmendNotes,
      found: amendment?.extensionNotes?.value,
      anchor: amendment?.extensionNotes?.anchor,
      hint: "Anything else the amendment changes — a new landlord, no further extensions, agreements running concurrently.",
    },
    {
      key: "amendUseExtension",
      label: "Uses an extension term",
      kind: "choice",
      value: amendUseExtension ? "Yes" : "No",
      setValue: (v) => setAmendUseExtension(v === "Yes"),
      hint: "Yes when this amendment exercises an extension the lease already carried. No when it grants a new one.",
    },
  ];

  const outstandingAmend = AMEND_ROWS.filter((r) => !reviewedAmend.has(r.key)).length;

  const settleAmend = (key: string) => {
    setReviewedAmend((prev) => new Set(prev).add(key));
    setOpenAmendKey(null);
  };

  const outstanding = rows.filter((f) => !reviewed.has(f.key)).length;
  // The amendment rows are anchored too, so their pencil shows the reviewer
  // the clause the figure came from — the same confirmation the lease rows give,
  // and the thing that makes a suggested value checkable rather than trusted.
  //
  // Not every amendment row can have one: the notes are assembled from several
  // clauses, and a document read from its PAGE IMAGES has no positions at all —
  // a model returns what it read, not where it was. Those rows simply don't
  // move the pane rather than jumping somewhere misleading.
  const anchor = focusKey
    ? extraction[focusKey]?.anchor ??
      AMEND_ROWS.find((row) => row.key === focusKey)?.anchor ??
      null
    : null;

  const openLocalFile = async (file: File) => {
    const buffer = await file.arrayBuffer();
    setBytes(new Uint8Array(buffer));
    setLeaseLabel(file.name);
    setLookup(null);
  };

  // The value itself, editable throughout — a reviewer who disagrees with both
  // the lease and the record types what's right rather than picking the least
  // wrong of two.
  const control = (field: LeaseReviewField) => {
    const shared = {
      id: `tosp-lease-${field.key}`,
      className: "manage-input tosp-lease-input",
      value: values[field.key] ?? "",
      autoComplete: "off",
    };
    const onChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ) => setValue(field.key, e.target.value);

    if (field.kind === "date") {
      return <input {...shared} type="date" onChange={onChange} />;
    }
    if (field.kind === "month") {
      return (
        <select {...shared} onChange={onChange}>
          <option value="">— Select a month —</option>
          {RENT_STEP_UP_MONTHS.map((month) => (
            <option key={month} value={month}>
              {month}
            </option>
          ))}
        </select>
      );
    }
    if (field.kind === "textarea") {
      return <textarea {...shared} rows={2} onChange={onChange} />;
    }
    return (
      <div className="tosp-affix-wrap">
        {field.kind === "money" && <span className="tosp-affix tosp-affix-prefix">$</span>}
        <input
          {...shared}
          className={`manage-input tosp-lease-input${
            field.kind === "money"
              ? " tosp-has-prefix"
              : field.kind === "percent"
                ? " tosp-has-suffix"
                : ""
          }`}
          inputMode={field.kind === "text" ? undefined : "decimal"}
          onChange={onChange}
        />
        {field.kind === "percent" && <span className="tosp-affix tosp-affix-suffix">%</span>}
      </div>
    );
  };

  // What the row offers once opened.
  //
  // Two figures to choose between get Keep and Update. Where there's nothing to
  // choose — the lease agrees, or found nothing at all — a single Confirm says
  // the admin has looked, which is the whole of what's being asked.
  const choice = (field: LeaseReviewField) => {
    const state = stateOf(field.key);
    const found = extraction[field.key];
    const confidence =
      found?.confidence !== undefined
        ? `${Math.round(found.confidence * 100)}% confident`
        : "";

    const note = found?.note ? <p className="tosp-lease-note">{found.note}</p> : null;
    // What the reviewer should be looking for. Only while the row is open —
    // seventeen hints on screen at once is noise, one is guidance.
    const hint = <p className="tosp-lease-choice-hint">{field.hint}</p>;

    if (state.kind === "none" || state.kind === "agrees") {
      return (
        <div className="tosp-lease-choice">
          <p className="tosp-lease-choice-line">
            {state.kind === "agrees"
              ? `The lease agrees with what's on the record${confidence ? ` · ${confidence}` : ""}.`
              : "Not found in the lease — check the document and enter it."}
          </p>
          {hint}
          {note}
          <div className="tosp-lease-choice-actions">
            <button
              type="button"
              className="tosp-choice-btn"
              onClick={() => settle(field.key, values[field.key] ?? "")}
            >
              Confirm
            </button>
          </div>
        </div>
      );
    }

    // A difference — either against a recorded value or against a blank one.
    const was = state.kind === "conflict" ? state.recorded : "";
    const says = state.kind === "conflict" ? state.suggested : found?.value ?? "";

    return (
      <div className="tosp-lease-choice">
        <div className="tosp-lease-choice-pair">
          <div>
            <span>On the record</span>
            <strong>{displayValue(field.kind, was)}</strong>
          </div>
          <div>
            <span>In the lease{confidence ? ` · ${confidence}` : ""}</span>
            <strong>{displayValue(field.kind, says)}</strong>
          </div>
        </div>
        {hint}
        {note}
        <div className="tosp-lease-choice-actions">
          <button
            type="button"
            className="tosp-choice-btn"
            onClick={() => settle(field.key, was)}
          >
            Keep
          </button>
          <button
            type="button"
            className="tosp-choice-btn"
            onClick={() => settle(field.key, says)}
          >
            Update
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="tosp-lease-review">
      <div className="tosp-lease-header">
        <div>
          <div className="tosp-lease-title">Review Lease</div>
          <div className="tosp-lease-subtitle">
            {[tenantValues.operatingName || tenantValues.legalEntity, leaseLabel]
              .filter(Boolean)
              .join(" · ") || "No lease open"}
          </div>
        </div>
        <button type="button" className="tosp-btn-secondary" onClick={onCancel}>
          Cancel review
        </button>
      </div>

      <div className="tosp-lease-body">
        <div className="tosp-lease-left">
          {bytes ? (
            <LeasePdfPane bytes={bytes} anchor={anchor} step={jump} />
          ) : loading ? (
            <div className="tosp-lease-pane tosp-lease-pane-empty">
              <p>Looking for the signed lease…</p>
            </div>
          ) : (
            <div className="tosp-lease-pane tosp-lease-pane-empty">
              <div className="tosp-lease-empty-card">
                <strong>No signed lease found.</strong>
                {/* The specific hop that failed, so it can be put right rather
                    than guessed at. */}
                <p>{lookup}</p>
                <p>You can open the lease from your machine to review it now.</p>
                <button
                  type="button"
                  className="tosp-btn-primary"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Open lease PDF
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void openLocalFile(file);
                    e.target.value = "";
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="tosp-lease-right">
          {/* Says plainly what state the review opened in, so a screen of green
              marks is never mistaken for work done in this sitting. */}
          {amendmentMode && (
            <div className="tosp-lease-carried">
              {reopeningAmendment ? (
                <>
                  Amendment last verified on{" "}
                  {formatDate(previous!.reviewedAt.slice(0, 10))}
                  {previous!.reviewedBy ? ` by ${previous!.reviewedBy}` : ""}. Its
                  changes are shown confirmed at the foot of the list — correct
                  any of them and verify again.{" "}
                </>
              ) : (
                <>
                  Lease was last verified on{" "}
                  {formatDate(previous!.reviewedAt.slice(0, 10))}
                  {previous!.reviewedBy ? ` by ${previous!.reviewedBy}` : ""}. Those
                  terms are kept as they stand and stay editable — confirm the
                  amendment's changes at the foot of the list.{" "}
                </>
              )}
              <button
                type="button"
                className="tosp-amendment-reverify"
                onClick={() => {
                  setFullReverify(true);
                  setReviewed(new Set());
                }}
              >
                {reopeningAmendment
                  ? "Re-verify the lease's own terms"
                  : "This is a replacement lease — re-verify everything"}
              </button>
            </div>
          )}
          {!amendmentMode && carried && (
            <div className="tosp-lease-carried">
              Verified on {formatDate(carried.at.slice(0, 10))} against this same
              document.
              {carried.again > 0
                ? ` ${carried.again} field${carried.again === 1 ? "" : "s"} need${
                    carried.again === 1 ? "s" : ""
                  } checking again.`
                : " Change whatever you need and save."}{" "}
              {/* Reviews signed off before the stamp recorded which kind it was
                  can't be told apart from a lease review, and the record can't
                  be made to say — a rent renewal on it might equally have been
                  typed in. So the admin says. Reviews from here on record it
                  themselves and reopen without being asked. */}
              <button
                type="button"
                className="tosp-amendment-reverify"
                onClick={openAppliedAmendment}
              >
                This document is an amendment — show its changes
              </button>
            </div>
          )}
          {!amendmentMode && previous && !carried && extracted && (
            <div className="tosp-lease-carried tosp-lease-carried-stale">
              This is a different document from the one verified on{" "}
              {formatDate(previous.reviewedAt.slice(0, 10))}, so every term needs
              checking again.
            </div>
          )}

          {/* Every term at once. A row carries its own state, so what's left to
              do is read off the column of marks rather than discovered by
              stepping through the list. */}
          <div className="tosp-lease-rows">
            {rows.map((field) => {
              // In amendment mode every verified term is carried as it stands,
              // so the whole list reads green — the work is in the panel below.
              const done = amendmentMode || reviewed.has(field.key);
              const isOpen = openKey === field.key;
              return (
                <div
                  key={field.key}
                  className={`tosp-lease-row${isOpen ? " tosp-lease-row-open" : ""}`}
                >
                  <div className="tosp-lease-row-head">
                    <span
                      className={`tosp-review-mark${
                        done ? " tosp-review-mark-done" : " tosp-review-mark-todo"
                      }`}
                      title={done ? "Reviewed" : "Not yet reviewed"}
                      aria-hidden="true"
                    />
                    <label
                      className="tosp-lease-row-label"
                      htmlFor={`tosp-lease-${field.key}`}
                    >
                      {field.label}
                    </label>
                    {control(field)}
                    <button
                      type="button"
                      className={`tosp-btn-pencil${isOpen ? " tosp-btn-pencil-open" : ""}`}
                      title={done ? "Review again" : field.hint}
                      aria-expanded={isOpen}
                      onClick={() => open(field.key)}
                    >
                      ✎
                    </button>
                  </div>

                  {isOpen && choice(field)}
                </div>
              );
            })}

          {/* The amendment's own changes — the only thing to confirm when the
              open document amends a lease already verified. Fed straight into
              the existing rent-reset mechanism on save, so the budget maths
              picks them up exactly as a manually-added renewal would. All in the
              amendment blue. */}
          {amendmentMode && (
            <>
              <div className="tosp-amendment-heading" ref={amendmentRef}>
                Amendment changes
                {readingAmendment && (
                  // Said out loud because reading the pages takes seconds, and
                  // four empty rows with no explanation read as "found nothing"
                  // rather than "still looking".
                  <span className="tosp-amendment-reading">
                    <span className="tosp-spinner" />
                    Reading the amendment…
                  </span>
                )}
              </div>

              {AMEND_ROWS.map((row) => {
                  const done = reviewedAmend.has(row.key);
                  const isOpen = openAmendKey === row.key;
                  const differs =
                    !!row.found?.trim() && row.found.trim() !== row.value.trim();
                  return (
                    <div
                      key={row.key}
                      className={`tosp-lease-row${isOpen ? " tosp-lease-row-open" : ""}`}
                    >
                      <div className="tosp-lease-row-head">
                        <span
                          className={`tosp-review-mark${
                            done ? " tosp-review-mark-done" : " tosp-review-mark-todo"
                          }`}
                          title={done ? "Reviewed" : "Not yet reviewed"}
                          aria-hidden="true"
                        />
                        <label
                          className="tosp-lease-row-label"
                          htmlFor={`tosp-amend-${row.key}`}
                        >
                          {row.label}
                        </label>

                        {row.kind === "choice" ? (
                          // Yes/No rather than a checkbox: an unticked box reads
                          // as "not decided", and this one has a real default
                          // that the admin should be able to see they accepted.
                          <div className="tosp-amend-choice-pair">
                            {["Yes", "No"].map((option) => (
                              <label key={option} className="tosp-amend-radio">
                                <input
                                  type="radio"
                                  name={`tosp-amend-${row.key}`}
                                  checked={row.value === option}
                                  onChange={() => row.setValue(option)}
                                />
                                {option}
                              </label>
                            ))}
                          </div>
                        ) : row.kind === "textarea" ? (
                          <textarea
                            id={`tosp-amend-${row.key}`}
                            className="manage-input tosp-lease-input"
                            rows={2}
                            value={row.value}
                            onChange={(e) => row.setValue(e.target.value)}
                          />
                        ) : row.kind === "money" ? (
                          <div className="tosp-affix-wrap">
                            <span className="tosp-affix tosp-affix-prefix">$</span>
                            <input
                              id={`tosp-amend-${row.key}`}
                              className="manage-input tosp-lease-input tosp-has-prefix"
                              autoComplete="off"
                              inputMode="decimal"
                              value={row.value}
                              onChange={(e) => row.setValue(e.target.value)}
                            />
                          </div>
                        ) : (
                          <input
                            id={`tosp-amend-${row.key}`}
                            type="date"
                            className="manage-input tosp-lease-input"
                            value={row.value}
                            onChange={(e) => row.setValue(e.target.value)}
                          />
                        )}

                        <button
                          type="button"
                          className={`tosp-btn-pencil${isOpen ? " tosp-btn-pencil-open" : ""}`}
                          title={done ? "Review again" : row.hint}
                          aria-expanded={isOpen}
                          onClick={() => {
                            setOpenAmendKey((current) =>
                              current === row.key ? null : row.key,
                            );
                            // Move the document pane to the clause this came
                            // from, exactly as the lease rows do. `jump` is
                            // bumped so reopening the same row scrolls back to
                            // it rather than sitting where the reader left off.
                            setFocusKey(row.key);
                            setJump((n) => n + 1);
                          }}
                        >
                          ✎
                        </button>
                      </div>

                      {isOpen && (
                        <div className="tosp-lease-choice">
                          <p className="tosp-lease-choice-line">
                            {differs
                              ? `The amendment reads “${row.found}”.`
                              : row.found?.trim()
                                ? "The amendment agrees with what's entered."
                                : "Not found in the amendment — read it and enter what it says."}
                          </p>
                          <p className="tosp-lease-choice-hint">{row.hint}</p>
                          {row.key === "amendUseExtension" &&
                            amendUseExtension &&
                            !unusedExtensions && (
                              <p className="tosp-lease-note">
                                No unused extension terms are recorded on this
                                lease — nothing will be marked.
                              </p>
                            )}
                          <div className="tosp-lease-choice-actions">
                            {differs && (
                              <button
                                type="button"
                                className="tosp-choice-btn"
                                onClick={() => {
                                  row.setValue(row.found!.trim());
                                  settleAmend(row.key);
                                }}
                              >
                                Update
                              </button>
                            )}
                            <button
                              type="button"
                              className="tosp-choice-btn"
                              onClick={() => settleAmend(row.key)}
                            >
                              {differs ? "Keep" : "Confirm"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </>
          )}
          </div>

          {/* Nothing is carried forward until every row has been dealt with —
              a half-reviewed lease landing on a good record is the one outcome
              worth making impossible, and a button that stays grey says so
              more plainly than a warning would. In amendment mode the terms are
              carried as verified, so the gate is the amendment panel, not the
              rows. */}
          <div className="tosp-lease-nav">
            <button
              type="button"
              className="tosp-btn-primary"
              // In amendment mode the lease's terms are carried as verified, so
              // the gate is the amendment's own rows — otherwise the button
              // would be live before a single change had been looked at.
              disabled={
                amendmentMode ? outstandingAmend > 0 : outstanding > 0
              }
              title={
                amendmentMode
                  ? outstandingAmend > 0
                    ? `${outstandingAmend} amendment change${
                        outstandingAmend === 1 ? "" : "s"
                      } still to confirm`
                    : undefined
                  : outstanding > 0
                    ? `${outstanding} field${outstanding === 1 ? "" : "s"} still to review`
                    : undefined
              }
              onClick={() => {
                const stamp: LeaseReviewStamp = {
                  // No document open means nothing to fingerprint, and an
                  // empty hash matches nothing — so a review done by hand
                  // against no lease is never carried forward.
                  documentHash: fingerprint ?? "",
                  reviewedAt: new Date().toISOString(),
                  ...(reviewerName ? { reviewedBy: reviewerName } : {}),
                  // In amendment mode the verified set is the terms carried
                  // forward — every row, since none were re-opened.
                  verified: amendmentMode
                    ? rows.map((f) => f.key)
                    : rows.map((f) => f.key).filter((key) => reviewed.has(key)),
                  // Recorded so reopening this document brings the amendment
                  // panel back rather than reading as an unchanged lease.
                  ...(amendmentMode ? { amendment: true } : {}),
                };
                if (amendmentMode) {
                  onFinish(values, stamp, {
                    spaceId: draft.leasedSpaces[0]?.id || "",
                    // Set only when this amendment has been applied before —
                    // its renewal is then corrected rather than duplicated.
                    ...(amendResetId ? { resetId: amendResetId } : {}),
                    rentCommenceDate: amendCommence,
                    rentPsfCommencement: amendPsf.trim(),
                    extensionNotes: amendNotes.trim(),
                    markExtensionUsed: amendUseExtension && unusedExtensions,
                  });
                } else {
                  onFinish(values, stamp);
                }
              }}
            >
              Verify
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
